import React, { ReactNode, createContext, useContext, useEffect, useState } from "react";
import { DatabaseNode, NumberDefinition } from "@lifeat/shared/data-models/definition";
import { UserCollection } from "@lifeat/shared/data-models/user/UserEntity";
import { DEFAULT_TIMER_VOLUME } from "@lifeat/shared/widget/PomodoroTimerConstants";
import { TimerModeType } from "@lifeat/shared/widget/PomodoroTimerTypes";
import { useDebounce } from "@hooks/useDebounce";
import { logError } from "@service/ConsoleLoggerService";
import { useDatabaseContext } from "./DatabaseContext";
import { useUserContext } from "./UserContext";

const VOLUME_CHANGE_DEBOUNCE_DELAY_MS = 2000;

export type TimerVolumeContext = {
    timerVolume: number;
    groupTimerVolume: number;
    changeTimerVolume: (timerMode: TimerModeType, value: number) => void;
};

export const TimerVolumeContext = createContext<TimerVolumeContext | null>(null);

interface TimerVolumeContextProviderProps {
    children: ReactNode;
}

export function TimerVolumeContextProvider({ children }: TimerVolumeContextProviderProps) {
    const { user, userData } = useUserContext();
    const db = useDatabaseContext();
    const [initialized, setInitialized] = useState(false);
    const [timerVolume, setTimerVolume] = useState(DEFAULT_TIMER_VOLUME);
    const [groupTimerVolume, setGroupTimerVolume] = useState(DEFAULT_TIMER_VOLUME);

    useEffect(() => {
        if (userData && !initialized) {
            setTimerVolume(userData.pomodoroWidgetSettings?.timerVolume ?? DEFAULT_TIMER_VOLUME);
            setGroupTimerVolume(userData.pomodoroWidgetSettings?.groupTimerVolume ?? DEFAULT_TIMER_VOLUME);
            setInitialized(true);
        }
    }, [initialized, userData]);

    const debouncedDatabaseUpdate = useDebounce(
        async <TPath extends string>(dbNode: DatabaseNode<NumberDefinition, TPath>, value: number) => {
            try {
                await db.set(dbNode, value);
            } catch {
                logError("Failed to save timer volume.");
            }
        },
        VOLUME_CHANGE_DEBOUNCE_DELAY_MS,
    );

    async function changeTimerVolume(mode: TimerModeType, value: number) {
        switch (mode) {
            case "personal":
                setTimerVolume(value);
                debouncedDatabaseUpdate(
                    UserCollection.at(user.uid).at("pomodoroWidgetSettings").at("timerVolume").node,
                    value,
                );
                break;
            case "group":
                setGroupTimerVolume(value);
                debouncedDatabaseUpdate(
                    UserCollection.at(user.uid).at("pomodoroWidgetSettings").at("groupTimerVolume").node,
                    value,
                );
                break;
        }
    }

    return (
        <TimerVolumeContext.Provider value={{ timerVolume, groupTimerVolume, changeTimerVolume }}>
            {children}
        </TimerVolumeContext.Provider>
    );
}

export function useTimerVolumeContext() {
    const context = useContext(TimerVolumeContext);
    if (context == null) {
        throw new Error("Missing TimerVolumeContext");
    }
    return context;
}
